import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CATS = ["service", "punctuality", "vehicle", "billing", "other"];
const SEVS = ["low", "medium", "high"];
const STATUSES = ["pending", "in_progress", "resolved"];

const inp = "w-full bg-transparent border-b border-white/20 px-0 py-2.5 text-sm text-white focus:border-white focus:outline-none";
const lbl = "block text-[10px] uppercase tracking-[0.25em] text-[#8E9AA6] mb-2";
const btn = "px-6 py-3 text-[11px] tracking-[0.25em] uppercase font-medium transition-all";

const statusPill = (s) => {
  const map = { pending: "bg-[#C5A24A]/20 text-[#E6C46E]", in_progress: "bg-white/15 text-white", resolved: "bg-white/5 text-[#8E9AA6]" };
  return `inline-block px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] ${map[s] || "bg-white/10 text-white"}`;
};
const sevPill = (s) => {
  const map = { low: "text-[#8E9AA6]", medium: "text-[#E6C46E]", high: "text-red-400" };
  return `text-[11px] uppercase tracking-[0.2em] ${map[s] || "text-white"}`;
};

function Modal({ open, title, onClose, children, testid }) {
  if (!open) return null;
  return (
    <div data-testid={testid} className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-[#121214] border border-white/10 p-8 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-serif text-xl font-semibold text-white">{title}</h3>
          <button onClick={onClose} className="text-[#8E9AA6] hover:text-white"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function AdminComplaints() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [items, setItems] = useState([]); const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1); const pageSize = 20;
  const [status, setStatus] = useState(""); const [category, setCategory] = useState("");
  const [severity, setSeverity] = useState(""); const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (status) params.status = status; if (category) params.category = category;
      if (severity) params.severity = severity; if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/admin/complaints", { params });
      setItems(data.items); setTotal(data.total);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  }, [page, status, category, severity, q]);
  useEffect(() => { load(); }, [load]);

  const removeOne = async (c) => {
    if (!window.confirm(`Delete complaint #${c.id.slice(0,8)}? This is permanent.`)) return;
    try { await api.delete(`/admin/complaints/${c.id}`); toast.success("Complaint deleted."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-8" data-testid="admin-complaints-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.4em] text-[#8E9AA6] mb-2">Operations · Service Quality</div>
          <h1 className="font-serif text-3xl md:text-4xl font-semibold tracking-tight text-white">Complaints Tracker</h1>
        </div>
        <button data-testid="complaints-log-btn" onClick={() => setCreateOpen(true)} className={`bg-white text-black hover:bg-[#E6E6E6] inline-flex items-center gap-2 ${btn}`}>
          <Plus size={14} /> Log Complaint
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <input data-testid="complaints-search" placeholder="Search description…" value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} className={inp} />
        <select value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} className={inp + " bg-[#0B0B0C]"}><option value="">All Statuses</option>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select>
        <select value={category} onChange={(e) => { setPage(1); setCategory(e.target.value); }} className={inp + " bg-[#0B0B0C]"}><option value="">All Categories</option>{CATS.map((c) => <option key={c} value={c}>{c}</option>)}</select>
        <select value={severity} onChange={(e) => { setPage(1); setSeverity(e.target.value); }} className={inp + " bg-[#0B0B0C]"}><option value="">All Severities</option>{SEVS.map((s) => <option key={s} value={s}>{s}</option>)}</select>
      </div>

      <div className="border border-white/10">
        <table className="w-full text-sm" data-testid="complaints-table">
          <thead className="bg-white/[0.03] text-[10px] uppercase tracking-[0.25em] text-[#8E9AA6]">
            <tr>
              <th className="text-left px-4 py-3 font-normal">ID</th>
              <th className="text-left px-4 py-3 font-normal">Client</th>
              <th className="text-left px-4 py-3 font-normal">Driver</th>
              <th className="text-left px-4 py-3 font-normal">Category</th>
              <th className="text-left px-4 py-3 font-normal">Severity</th>
              <th className="text-left px-4 py-3 font-normal">Status</th>
              <th className="text-left px-4 py-3 font-normal">Created</th>
              <th className="text-right px-4 py-3 font-normal">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-4 py-10 text-center text-[#8E9AA6] text-xs">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-[#8E9AA6] text-xs">No complaints.</td></tr>}
            {!loading && items.map((c) => (
              <tr key={c.id} data-testid={`complaint-row-${c.id}`} className="border-t border-white/5 hover:bg-white/[0.02]">
                <td className="px-4 py-3 text-white text-xs">#{c.id.slice(0,8)}</td>
                <td className="px-4 py-3 text-[#E6E6E6]">{c.client_name || "—"}</td>
                <td className="px-4 py-3 text-[#E6E6E6]">{c.driver_name || "—"}</td>
                <td className="px-4 py-3 text-[#8E9AA6]">{c.category}</td>
                <td className="px-4 py-3"><span className={sevPill(c.severity)}>{c.severity}</span></td>
                <td className="px-4 py-3"><span className={statusPill(c.status)}>{c.status.replace("_", " ")}</span></td>
                <td className="px-4 py-3 text-[#8E9AA6] text-xs">{c.created_at?.slice(0,10)}</td>
                <td className="px-4 py-3 text-right space-x-3">
                  <button data-testid={`complaint-edit-${c.id}`} onClick={() => setEditTarget(c)} className="text-[#8E9AA6] hover:text-white" title="Edit"><Pencil size={14} /></button>
                  {isAdmin && <button data-testid={`complaint-delete-${c.id}`} onClick={() => removeOne(c)} className="text-[#8E9AA6] hover:text-red-400" title="Delete"><Trash2 size={14} /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-xs text-[#8E9AA6]">
        <div>{total} total · page {page} of {Math.max(1, Math.ceil(total / pageSize))}</div>
        <div className="flex gap-2">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className={`${btn} border border-white/15 text-white disabled:opacity-30`}>Prev</button>
          <button disabled={page * pageSize >= total} onClick={() => setPage((p) => p + 1)} className={`${btn} border border-white/15 text-white disabled:opacity-30`}>Next</button>
        </div>
      </div>

      <ComplaintFormModal open={createOpen} onClose={() => setCreateOpen(false)} onSaved={load} mode="create" />
      <ComplaintFormModal open={!!editTarget} onClose={() => setEditTarget(null)} onSaved={load} mode="edit" target={editTarget} />
    </div>
  );
}

function ComplaintFormModal({ open, mode, target, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [f, setF] = useState({}); const [busy, setBusy] = useState(false);
  useEffect(() => {
    setF(isEdit && target
      ? { ...target }
      : { booking_id: "", driver_id: "", category: "service", severity: "medium", status: "pending", description: "", resolution_notes: "" });
  }, [isEdit, target, open]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (isEdit) {
        const upd = {
          category: f.category, severity: f.severity, description: f.description,
          status: f.status, resolution_notes: f.resolution_notes,
        };
        await api.patch(`/admin/complaints/${target.id}`, upd);
        toast.success("Complaint updated.");
      } else {
        const body = { ...f };
        if (!body.booking_id) delete body.booking_id;
        if (!body.driver_id) delete body.driver_id;
        await api.post("/admin/complaints", body);
        toast.success("Complaint logged.");
      }
      onSaved(); onClose();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const advance = (next) => setF({ ...f, status: next });
  return (
    <Modal open={open} onClose={onClose} title={isEdit ? `Complaint #${target?.id?.slice(0,8)}` : "Log a complaint"} testid="complaint-form-modal">
      <form onSubmit={submit} data-testid="complaint-form" className="space-y-5">
        {!isEdit && (
          <>
            <div><label className={lbl}>Booking ID (optional)</label><input data-testid="cmp-booking-id" className={inp} value={f.booking_id || ""} onChange={set("booking_id")} placeholder="UUID — auto-fills client + driver" /></div>
            <div><label className={lbl}>Driver ID (optional, override)</label><input data-testid="cmp-driver-id" className={inp} value={f.driver_id || ""} onChange={set("driver_id")} /></div>
          </>
        )}
        <div className="grid grid-cols-2 gap-5">
          <div><label className={lbl}>Category</label><select data-testid="cmp-category" className={inp + " bg-[#0B0B0C]"} value={f.category} onChange={set("category")}>{CATS.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
          <div><label className={lbl}>Severity</label><select data-testid="cmp-severity" className={inp + " bg-[#0B0B0C]"} value={f.severity} onChange={set("severity")}>{SEVS.map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
        </div>
        <div><label className={lbl}>Description</label><textarea data-testid="cmp-description" rows={4} required className={inp + " resize-none"} value={f.description || ""} onChange={set("description")} /></div>
        {isEdit && (
          <>
            <div>
              <label className={lbl}>Status</label>
              <div className="flex gap-2" data-testid="cmp-status-pills">
                {STATUSES.map((s, i) => (
                  <button type="button" key={s} onClick={() => advance(s)} className={`flex-1 px-3 py-2 text-[10px] uppercase tracking-[0.22em] border transition-all ${f.status === s ? "bg-white text-black border-white" : "bg-transparent text-[#8E9AA6] border-white/20 hover:text-white"}`}>
                    {i + 1}. {s.replace("_", " ")}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className={lbl}>Resolution notes {f.status === "resolved" && <span className="text-red-400">(required)</span>}</label>
              <textarea data-testid="cmp-resolution" rows={3} className={inp + " resize-none"} value={f.resolution_notes || ""} onChange={set("resolution_notes")} />
            </div>
            <div className="text-xs text-[#8E9AA6] grid grid-cols-3 gap-3 pt-3 border-t border-white/5">
              <div><div className="text-[9px] uppercase tracking-wider text-[#8E9AA6]/70">Created</div>{target?.created_at?.slice(0, 16).replace("T", " ")}</div>
              <div><div className="text-[9px] uppercase tracking-wider text-[#8E9AA6]/70">Updated</div>{target?.updated_at?.slice(0, 16).replace("T", " ")}</div>
              <div><div className="text-[9px] uppercase tracking-wider text-[#8E9AA6]/70">Resolved</div>{target?.resolved_at?.slice(0, 16).replace("T", " ") || "—"}</div>
            </div>
          </>
        )}
        <div className="pt-4 flex justify-end gap-3">
          <button type="button" onClick={onClose} className={`${btn} border border-white/15 text-white`}>Cancel</button>
          <button type="submit" disabled={busy} data-testid="cmp-submit" className={`${btn} bg-white text-black hover:bg-[#E6E6E6] disabled:opacity-50`}>{busy ? "Saving…" : isEdit ? "Save" : "Log"}</button>
        </div>
      </form>
    </Modal>
  );
}
